import { StandardError } from "@core-ts/StandardError"

export const startupCommand: string | null = null

export class ConfigurationSet {
    // Sub-configs

    subConfigs = new Map<string, ConfigurationSet>()
    strings = new Map<string, string>()
    numbers = new Map<string, number>()
    booleans = new Map<string, boolean>()

    getString(name: string, _default: string | undefined = undefined): string {
        const value = this.strings.get(name)
        if (value !== undefined) return value
        if (_default !== undefined) return _default

        throw new StandardError("Cannot find setting " + name)
    }
    getStringWithUndefined(name: string, _default: string | undefined): string | undefined {
        const value = this.strings.get(name)
        if (value !== undefined) return value

        return _default
    }
    getStringOrNull(name: string): string | null {
        const value = this.strings.get(name)
        if (value === undefined) return null
        return value
    }

    getNumber(name: string, _default: number | undefined = undefined): number {
        const value = this.numbers.get(name)
        if (value !== undefined) return value
        if (_default !== undefined) return _default

        throw new StandardError("Cannot find setting " + name)
    }
    getNumberOrNull(name: string): number | null {
        const value = this.numbers.get(name)
        if (value === undefined) return null
        return value
    }

    getBoolean(name: string, _default: boolean | undefined = undefined): boolean {
        const value = this.booleans.get(name)
        if (value !== undefined) return value
        if (_default !== undefined) return _default

        throw new StandardError("Cannot find setting " + name)
    }
    getBooleanOrNull(name: string): boolean | null {
        const value = this.booleans.get(name)
        if (value === undefined) return null
        return value
    }

    getConfig(name: string): ConfigurationSet {
        const config = this.getConfigOrNull(name)
        if (config !== null) return config

        throw new StandardError("Cannot find subconfig " + name)
    }

    getConfigOrNull(name: string): ConfigurationSet | null {
        const config = this.subConfigs.get(name)
        if (config === undefined) return null

        return config
    }
}

// This is set by config.js and therefor needs to be declared as a var
// biome-ignore lint/style/noVar: <explanation>
declare var frontendConfig: Record<string, unknown>

export function loadFrontendConfig(): ConfigurationSet {
    // We've stated that frontendConfig is always defined but it can actually be undefined if we can't load config.js or similar.
    if (frontendConfig === undefined || frontendConfig === null) throw new StandardError("Missing frontendConfig")
    if (typeof frontendConfig !== "object") throw new StandardError("frontendConfig must be an object")

    // For historical reasons, the frontendConfig is structured differently than the other configs
    // Change the structure here to match the other configs
    if ("instanceName" in frontendConfig) {
        const { instanceName: rawInstanceName, ...instanceConfig } = frontendConfig
        const instanceName = rawInstanceName as string

        frontendConfig = {
            instances: {},
        }
        ;(frontendConfig as Record<string, Record<string, unknown>>).instances[instanceName] = instanceConfig
        //console.log("Converted frontendConfig to new format. New config is: ", JSON.stringify(frontendConfig))
    }

    const config = parseNodeFrontend("config.js", frontendConfig)
    if (config === null) throw new StandardError("Cannot load config")

    return config
}

export function parseNodeFrontend(filename: string, node: Record<string, unknown>): ConfigurationSet {
    const ret = new ConfigurationSet()

    for (const [key, value] of Object.entries(node)) {
        if (typeof key !== "string")
            throw new StandardError("All keys in configuration must be strings in file " + filename)

        if (key === "include" || key === "include_if_exists") {
            throw new StandardError("include directive not allowed in frontend config")
        }

        if (Array.isArray(value)) {
            if (value.length === 2 && value[0] === "include_text_file") {
                throw new StandardError("include_text_file directive not allowed in frontend config")
            }
            throw new StandardError("Unparsable array directive in config in file " + filename)
        }

        if (typeof value === "string") {
            ret.strings.set(key, value)
        } else if (typeof value === "number") {
            ret.numbers.set(key, value)
        } else if (typeof value === "boolean") {
            ret.booleans.set(key, value)
        } else if (typeof value === "object" && value !== null) {
            ret.subConfigs.set(key, parseNodeFrontend(filename, value as Record<string, unknown>))
        } else {
            throw new StandardError(
                "All values in configuration must be strings, numbers or subobjects in file " + filename,
            )
        }
    }

    return ret
}
